<template>
  <div class="payment-status-stepper">
    <div class="tw-cursor-pointer tw-flex tw-items-center tw-gap-2 tw-mb-3">
      <div class="status-text">{{ errorTitle }}</div>
      <template v-if="isError">
        <SvgIcon name="info" size="14px" class="tw-text-red-600" />
        <q-popup-proxy ref="popupProxy" v-if="(isExceedError || isCreateError1C || isDocsNotSigned ) && errors?.length <= 1">
          <q-banner class="tw-p-2">
            <template v-if="isExceedError && payInfo">
              <div class="tw-font-medium tw-text-slate-800 tw-mb-2">
                Сумма платежа превышает Сумму договора
              </div>
              <div class="exceed-pay-block tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3">
                <div class="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                  <div class="tw-text-sm tw-text-slate-500">Cумма всех платежей:</div>
                  <div class="tw-text-sm tw-font-medium">
                    <Amount :value="exceedPaymentAmount" />
                  </div>
                </div>

                <div class="pay-info-bar">
                  <div v-if="exceedAgreementRatio > 0" class="pay-info-bar__paid" :style="exceedAgreementBarStyle" />
                  <div v-if="exceedOverageRatio > 0" class="pay-info-bar__remaining" :style="exceedOverageBarStyle" />
                </div>
                <div class="tw-mt-2 tw-flex tw-flex-wrap tw-gap-3 tw-items-end tw-justify-between">
                  <div class="tw-flex tw-items-center tw-gap-1">
                    <span class="tw-text-sm tw-text-slate-500">Сумма договора:</span>
                    <span class="tw-text-sm tw-font-medium green">
                      <Amount :value="exceedAgreementSum" />
                    </span>
                  </div>
                  <div class="tw-flex tw-items-center tw-gap-1">
                    <span class="tw-text-sm tw-text-slate-500">Превышения:</span>
                    <span class="tw-text-sm tw-font-medium tw-text-red-600">
                      <Amount :value="exceedOverageSum" />
                    </span>
                  </div>
                </div>
              </div>
              <div class="tw-text-sm tw-text-slate-500 tw-mt-2">
                Необходимо создать ДС
              </div>
            </template>
            <template v-else-if="isCreateError1C && errorBiText">
              <div class="tw-text-sm tw-text-slate-800 tw-font-medium">Ошибка с 1С:</div>
              <div class="error-bi-text">{{ errorBiText }}</div>
            </template>
            <template v-else-if="isDocsNotSigned && addInfo?.length">
              <div class="tw-text-xs tw-text-slate-800">
                Подпишите документы, чтобы разблокировать оплату:
              </div>
              <div class="docs-not-signed-divider" />
              <div class="tw-flex tw-flex-col tw-gap-2">
                <div
                  v-for="doc in addInfo"
                  :key="doc.floor_map_document_id"
                  class="docs-not-signed-row"
                >
                  <div class="tw-flex tw-items-center tw-gap-2">
                    <SvgIcon name="document_pen" size="16px" class="docs-not-signed-icon" color="#0849C6" />
                    <span class="tw-text-xs tw-text-slate-800">{{ doc.floor_map_document_type_name }}</span>
                    <q-icon
                      v-if="doc.assign_signs?.length"
                      name="info"
                      size="16px"
                      class="tw-cursor-pointer tw-text-slate-500 docs-not-signed-info-icon"
                      @click.stop
                    >
                      <q-popup-proxy>
                        <q-banner class="tw-p-3 tw-min-w-[280px]">
                          <div class="tw-text-sm tw-font-medium tw-text-slate-800 tw-mb-2">Подписанты</div>
                          <div class="tw-flex tw-flex-col tw-gap-2">
                            <div
                              v-for="signer in doc.assign_signs"
                              :key="signer.assign_type_id"
                              class="tw-text-xs tw-border-b tw-border-slate-100 tw-pb-2 tw-last:border-0 tw-last:pb-0"
                            >
                              <div class="tw-font-medium tw-text-slate-800">{{ signer.assign_type_name }}</div>
                              <div class="tw-text-slate-600">{{ signer.employee_fio }}</div>
                              <div class="tw-mt-1" :class="signer.is_signed ? 'green' : 'tw-text-amber-600'">
                                {{ signer.is_signed ? `Подписан ${signer.sign_date || ''}` : 'Ожидает подписи' }}
                              </div>
                            </div>
                          </div>
                        </q-banner>
                      </q-popup-proxy>
                    </q-icon>
                  </div>
                  <div class="tw-flex tw-items-center tw-gap-1">
                    <div class="docs-not-signed-status-icon">
                      <q-icon name="schedule" size="14px" color="white" />
                    </div>
                    <span class="tw-text-xs tw-text-slate-800">{{ doc.can_sign && !hasCurrentUserSigned(doc) ? "Ожидает вашей подписи" : "Ожидание подписантов" }}</span>
                  </div>
                  <template v-if="hasCurrentUserSigned(doc)">
                    <span class="tw-text-xs green tw-font-medium">Подписано</span>
                  </template>
                  <q-btn
                    v-else-if="doc.can_sign"
                    unelevated
                    no-caps
                    style="background-color: #0849C6; color: white;"
                    size="sm"
                    class="docs-not-signed-btn"
                    :loading="getSigningLoading(doc)"
                    @click="onSignDocument(doc)"
                  >
                    Подписать
                  </q-btn>
                </div>
              </div>
            </template>
            <div v-else></div>
          </q-banner>
        </q-popup-proxy>
      </template>
    </div>
    <div class="stepper-row">
      <div 
        v-for="(step, index) in steps" 
        :key="index"
        class="step-wrapper"
      >
        <div
          class="step-icon"
          :class="getStepClass(step, index)"
        >
          <q-tooltip
            content-class="step-icon-tooltip"
            anchor="top middle"
            self="bottom middle"
          >
            {{ getStepLabel(index) }}
          </q-tooltip>
          <q-icon 
            v-if="showCompletedIcon(index)" 
            name="check" 
            size="8px" 
            color="white"
          />
          <q-icon 
            v-else-if="isError && index === currentStep" 
            name="close" 
            size="8px" 
            color="white"
          />
          <q-icon 
            v-else
            name="check" 
            size="8px" 
            color="white"
          />
        </div>
        <div 
          v-if="index < steps.length - 1" 
          class="step-line"
          :class="{ 'active': step.completed, 'error': isError && index === currentStep }"
        />
      </div>
    </div>
    <div v-if="subtitle && !errors?.length" class="status-subtitle" v-html="subtitle"></div>
    <div v-if="isPaymentExpired" class="status-subtitle error-payment-expired">Задержка оплаты, свяжитесь с фин. менеджером</div>
    <div v-if="this.errors?.length" class="status-subtitle">
      <div v-for="error in errors" :key="error">
        <div :key="error.error_code" class="tw-flex tw-items-center tw-gap-1 tw-cursor-pointer">{{ error?.error_desc }} 
          <div v-if="error?.error_code === 'CREATE_ERROR_EXCEED' || error?.error_code === 'ERR_DOCS_NOT_SIGNED' || error?.documents?.length">
            <SvgIcon name="info" size="14px" class="tw-text-red-600" />
            <q-popup-proxy ref="popupProxy">
              <q-banner class="tw-p-2">
                <template v-if="error?.error_code === 'CREATE_ERROR_EXCEED' && payInfo">
                  <div class="tw-font-medium tw-text-slate-800 tw-mb-2">
                    Сумма платежа превышает Сумму договора
                  </div>
                  <div class="exceed-pay-block tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3">
                    <div class="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                      <div class="tw-text-sm tw-text-slate-500">Cумма всех платежей:</div>
                      <div class="tw-text-sm tw-font-medium">
                        <Amount :value="exceedPaymentAmount" />
                      </div>
                    </div>

                    <div class="pay-info-bar">
                      <div v-if="exceedAgreementRatio > 0" class="pay-info-bar__paid" :style="exceedAgreementBarStyle" />
                      <div v-if="exceedOverageRatio > 0" class="pay-info-bar__remaining" :style="exceedOverageBarStyle" />
                    </div>
                    <div class="tw-mt-2 tw-flex tw-flex-wrap tw-gap-3 tw-items-end tw-justify-between">
                      <div class="tw-flex tw-items-center tw-gap-1">
                        <span class="tw-text-sm tw-text-slate-500">Сумма договора:</span>
                        <span class="tw-text-sm tw-font-medium green">
                          <Amount :value="exceedAgreementSum" />
                        </span>
                      </div>
                      <div class="tw-flex tw-items-center tw-gap-1">
                        <span class="tw-text-sm tw-text-slate-500">Превышения:</span>
                        <span class="tw-text-sm tw-font-medium tw-text-red-600">
                          <Amount :value="exceedOverageSum" />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="tw-text-sm tw-text-slate-500 tw-mt-2">
                    Необходимо создать ДС
                  </div>
                </template>
                <template v-else-if="error?.error_code === 'ERR_DOCS_NOT_SIGNED' && addInfo?.length">
                  <div class="tw-text-xs tw-text-slate-800">
                    Подпишите документы, чтобы разблокировать оплату:
                  </div>
                  <div class="docs-not-signed-divider" />
                  <div class="tw-flex tw-flex-col tw-gap-2">
                    <div
                      v-for="doc in addInfo"
                      :key="doc.floor_map_document_id"
                      class="docs-not-signed-row"
                    >
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <SvgIcon name="document_pen" size="16px" class="docs-not-signed-icon" color="#0849C6" />
                        <span class="tw-text-xs tw-text-slate-800">{{ doc.floor_map_document_type_name }}</span>
                        <q-icon
                          v-if="doc.assign_signs?.length"
                          name="info"
                          size="16px"
                          class="tw-cursor-pointer tw-text-slate-500 docs-not-signed-info-icon"
                          @click.stop
                        >
                          <q-popup-proxy>
                            <q-banner class="tw-p-3 tw-min-w-[280px]">
                              <div class="tw-text-sm tw-font-medium tw-text-slate-800 tw-mb-2">Подписанты</div>
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <div
                                  v-for="signer in doc.assign_signs"
                                  :key="signer.assign_type_id"
                                  class="tw-text-xs tw-border-b tw-border-slate-100 tw-pb-2 tw-last:border-0 tw-last:pb-0"
                                >
                                  <div class="tw-font-medium tw-text-slate-800">{{ signer.assign_type_name }}</div>
                                  <div class="tw-text-slate-600">{{ signer.employee_fio }}</div>
                                  <div class="tw-mt-1" :class="signer.is_signed ? 'green' : 'tw-text-amber-600'">
                                    {{ signer.is_signed ? `Подписан ${signer.sign_date || ''}` : 'Ожидает подписи' }}
                                  </div>
                                </div>
                              </div>
                            </q-banner>
                          </q-popup-proxy>
                        </q-icon>
                      </div>
                      <div class="tw-flex tw-items-center tw-gap-1">
                        <div class="docs-not-signed-status-icon">
                          <q-icon name="schedule" size="14px" color="white" />
                        </div>
                        <span class="tw-text-xs tw-text-slate-800">{{ doc.can_sign && !hasCurrentUserSigned(doc) ? "Ожидает вашей подписи" : "Ожидание подписантов" }}</span>
                      </div>
                      <template v-if="hasCurrentUserSigned(doc)">
                        <span class="tw-text-xs green tw-font-medium">Подписано</span>
                      </template>
                      <q-btn
                        v-else-if="doc.can_sign"
                        unelevated
                        no-caps
                        style="background-color: #0849C6; color: white;"
                        size="sm"
                        class="docs-not-signed-btn"
                        :loading="getSigningLoading(doc)"
                        @click="onSignDocument(doc)"
                      >
                        Подписать
                      </q-btn>
                    </div>
                  </div>
                </template>
                <template v-else-if="error?.documents?.length">
                  <div class="tw-text-sm tw-text-slate-800 tw-font-semibold tw-px-2 tw-pb-2" v-if="error?.error_desc">
                    {{ error?.error_desc || '' }}:
                  </div>
                  <div v-for="doc in error?.documents" :key="doc.floor_map_document_id" class="tw-text-sm tw-text-slate-800 tw-px-2 tw-py-1">
                    {{ `АВР №${doc.avr_number}, ID документа: ${doc.floor_map_document_id}` }}
                  </div>
                </template>
                <div v-else></div>
              </q-banner>
            </q-popup-proxy>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SvgIcon from '@/components/common/SvgIcon';
import Amount from '@/components/common/Amount.vue';
import { useAppStore } from '@/services/store/app';

export default {
  name: 'PaymentStatusStepper',
  components: {
    SvgIcon,
    Amount
  },
  props: {
    statusCode: {
      type: String,
      required: true
    },
    errorMessage: {
      type: String,
      default: ''
    },
    errorTitle: {
      type: String,
      default: ''
    },
    errorCode: {
      type: String,
      default: ''
    },
    payInfo: {
      type: Object,
      default: null
    },
    addInfo: {
      default: null
    },
    signing: {
      type: Object,
      default: () => ({})
    },
    errors: {
      type: Array,
      default: () => []
    },
    isPaymentExpired: {
      type: Boolean,
      default: false
    }
  },
  emits: ['sign-document'],
  computed: {
    isDocsNotSigned() {
      return this.errorCode === 'ERR_DOCS_NOT_SIGNED'
    },
    isCreateError1C() {
      return this.statusCode === 'CREATE_ERROR_1C'
    },
    errorBiText() {
      const text = this.addInfo
      return typeof text === 'string' ? text.trim() : ''
    },
    isExceedError() {
      return this.statusCode === 'CREATE_ERROR_EXCEED'
    },
    exceedPaymentAmount() {
      if (!this.payInfo) return 0
      const payed = Number(this.payInfo.payed_sum_sbs ?? this.payInfo.payed_sum) ?? 0
      const unpayed = Number(this.payInfo.unpayed_sum) ?? 0
      return payed + unpayed
    },
    exceedAgreementSum() {
      return Number(Math.round(this.payInfo?.agreement_sum_without_warranty)) ?? 0
    },
    exceedRemainingSum() {
      const total = this.exceedAgreementSum
      const paid = Number(this.payInfo.payed_sum_sbs ?? this.payInfo.payed_sum) ?? 0
      return Math.max(0, total - paid)
    },
    exceedOverageSum() {
      const unpayed = Number(this.payInfo?.unpayed_sum) ?? 0
      const remaining = this.exceedRemainingSum
      return Math.max(0, unpayed - remaining)
    },
    exceedAgreementRatio() {
      const total = this.exceedPaymentAmount
      if (total <= 0) return 0
      return Math.max(0, Math.min(1, this.exceedAgreementSum / total))
    },
    exceedOverageRatio() {
      const total = this.exceedPaymentAmount
      if (total <= 0) return 0
      return Math.max(0, Math.min(1, this.exceedOverageSum / total))
    },
    exceedAgreementBarStyle() {
      return { width: `${this.exceedAgreementRatio * 100}%` }
    },
    exceedOverageBarStyle() {
      return { width: `${this.exceedOverageRatio * 100}%` }
    },
    currentStep() {
      const statusMap = {
        'CREATED': 0,
        'CREATE_ERROR': 1,
        'CREATE_ERROR_EXCEED': 1,
        'CREATED_IN_1C': 1,
        'CREATE_ERROR_1C': 2,
        'AWATING_PAYMENT': 2,
        'PAYED': 3
      }
      return statusMap[this.statusCode] ?? 0
    },
    isError() {
      return this.statusCode?.toLocaleLowerCase()?.includes('error')
    },
    subtitle() {
      if(this.errorMessage?.includes(',')) {
        return this.errorMessage?.split(',').join('<br>')
      }
      if (this.errorMessage) {
        return this.errorMessage
      }
      return ''
    },
    steps() {
      return [
        { completed: this.currentStep >= 0 },
        { completed: this.currentStep >= 1 },
        { completed: this.currentStep >= 2 },
        { completed: this.currentStep >= 3 }
      ]
    }
  },
  methods: {
    getSigningLoading(doc) {
      const assignType = doc.assign_signs?.find((s) => s.can_sign && !s.is_signed)
      const key = doc.floor_map_document_id + '_' + (assignType?.assign_type_id ?? '')
      return !!this.signing[key]
    },
    hasCurrentUserSigned(doc) {
      const appStore = useAppStore()
      const employeeId = appStore.userData?.employee_id
      if (employeeId == null || !doc.assign_signs?.length) return false
      return doc.assign_signs.some((s) => s.employee_id === employeeId && s.is_signed)
    },
    onSignDocument(doc) {
      this.$emit('sign-document', doc)
    },
    showCompletedIcon(index) {
      return this.currentStep >= index ? this.isError ? this.currentStep > index : true : false
    },
    getStepClass(step, index) {
      if (this.isError && index === this.currentStep) {
        return 'error'
      }
      if(this.isPaymentExpired && index === this.currentStep)
        return 'expired'
      return step.completed ? 'completed' : 'pending'
    },
    getStepLabel(index) {
      const labels = [
        'Создана заявка в SBS',
        'Заявка на оплату создана в 1С',
        'Ожидается оплата',
        'Оплачено'
      ]
      return labels[index] ?? ''
    }
  }
}
</script>

<style scoped lang="scss">
.payment-status-stepper {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  min-width: 300px;
  max-width: 500px;
}

.status-text {
  font-family: 'Open Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #212121;
}

.stepper-row {
  display: flex;
  align-items: center;
  gap: 2px;
  width: 100%;
}

.step-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
  
  &:last-child {
    flex: 0;
  }
}

.step-icon {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &.completed {
    background-color: #16A34A;
  }

  &.expired {
    background-color: #ff8b2d;
  }
  
  &.pending {
    background-color: #CBD5E1;
  }
  
  &.error {
    background-color: #DC2626;
  }
}

.step-line {
  flex: 1;
  height: 2px;
  margin: 0 3px;
  background-image: linear-gradient(to right, #CBD5E1 26%, transparent 0%);
  background-position: top;
  background-size: 12px 2px;
  background-repeat: repeat-x;

  &.active {
    background-image: linear-gradient(to right, #16A34A 26%, transparent 0%);
  }

  &.error {
    background-image: linear-gradient(to right, #DC2626 26%, transparent 0%);
  }
}

.status-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-style: italic;
  font-weight: 400;
  line-height: 16px;
  color: #64748B;
  margin-top: 7px;
  line-height: 1.6;
  letter-spacing: 0.4px;

  &.error-payment-expired {
    color: #dc2626;
    margin-top: 0;
  }
}

.pay-info-bar {
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 9999px;
  overflow: hidden;
  display: flex;
  gap: 5px;
}

.pay-info-bar__paid {
  height: 100%;
  background: #16a34a;
  border-radius: 9999px;
}

.pay-info-bar__remaining {
  height: 100%;
  background: #dc2626;
  border-radius: 9999px;
}

.error-bi-text {
  font-size: 13px;
  color: #dc2626;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 360px;
  font-weight: 500;
  margin-top: 5px;
  line-height: 1.4;
}

.docs-not-signed-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 12px 0;
}

.docs-not-signed-row {
  display: grid;
  grid-template-columns: 150px 205px 88px;
  align-items: center;
  gap: 12px;
}

.docs-not-signed-icon {
  color: #2563eb;
}

.docs-not-signed-status-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f97316;
  display: flex;
  align-items: center;
  justify-content: center;
}

.docs-not-signed-btn {
  margin-left: auto;
  border-radius: 8px;
}

.docs-not-signed-info-icon {
  margin-left: 2px;
  &:hover {
    color: #0849C6;
  }
}
</style>

<style lang="scss">
.step-icon-tooltip {
  background: white !important;
  color: #212121;
  font-size: 12px;
  padding: 6px 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
</style>
